"use client"

import { useCallback, useEffect, useState } from "react"
import Image from "next/image"
import useEmblaCarousel from "embla-carousel-react"

import { cn } from "@/lib/utils"

export type CarouselSlide = { src: string; caption: string }

const INTERVAL = 5500

/**
 * The screens of a project, one at a time. Swipe, arrows or the keyboard;
 * it advances on its own unless the reader is pointing at it, focused in it,
 * or has asked for less motion. The bar under the active number fills as
 * the next slide approaches.
 */
export function ProjectCarousel({
  slides,
  onOpen,
  labels,
}: {
  slides: CarouselSlide[]
  onOpen: (src: string) => void
  labels: { previous: string; next: string; region: string }
}) {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, duration: 28 })
  const [current, setCurrent] = useState(0)
  const [paused, setPaused] = useState(false)
  const [reduced, setReduced] = useState(false)

  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches)
  }, [])

  useEffect(() => {
    if (!embla) return
    const onSelect = () => setCurrent(embla.selectedScrollSnap())
    onSelect()
    embla.on("select", onSelect)
    return () => {
      embla.off("select", onSelect)
    }
  }, [embla])

  const auto = !paused && !reduced && slides.length > 1
  useEffect(() => {
    if (!embla || !auto) return
    const t = setTimeout(() => embla.scrollNext(), INTERVAL)
    return () => clearTimeout(t)
  }, [embla, auto, current])

  const prev = useCallback(() => embla?.scrollPrev(), [embla])
  const next = useCallback(() => embla?.scrollNext(), [embla])

  return (
    <section
      aria-roledescription="carousel"
      aria-label={labels.region}
      className="mt-12"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowLeft") prev()
        if (e.key === "ArrowRight") next()
      }}
    >
      <div ref={emblaRef} className="overflow-hidden border border-border">
        <div className="flex touch-pan-y">
          {slides.map((slide, i) => (
            <div
              key={slide.src}
              className="min-w-0 flex-[0_0_100%]"
              role="group"
              aria-roledescription="slide"
              aria-label={`${i + 1} / ${slides.length}`}
            >
              <button
                type="button"
                onClick={() => onOpen(slide.src)}
                className="block w-full"
                aria-label={slide.caption}
                tabIndex={i === current ? 0 : -1}
              >
                <Image
                  src={slide.src}
                  alt={slide.caption}
                  width={1600}
                  height={1000}
                  priority={i === 0}
                  sizes="(max-width: 1200px) 100vw, 1300px"
                  className="aspect-[16/10] w-full object-cover object-top"
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-[1fr_auto] items-start gap-x-6 gap-y-3">
        <p className="t-small flex min-h-[2.8em] max-w-[70ch] gap-3 text-muted-foreground" aria-live="polite">
          <span className="tabular-nums text-accent">
            {String(current + 1).padStart(2, "0")}
            <span className="text-muted-foreground">/{String(slides.length).padStart(2, "0")}</span>
          </span>
          <span>{slides[current]?.caption}</span>
        </p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={prev}
            aria-label={labels.previous}
            className="flex h-10 w-10 items-center justify-center border border-foreground text-foreground transition-colors hover:bg-foreground hover:text-background"
          >
            <span aria-hidden="true">&#8592;</span>
          </button>
          <button
            type="button"
            onClick={next}
            aria-label={labels.next}
            className="flex h-10 w-10 items-center justify-center border border-foreground text-foreground transition-colors hover:bg-foreground hover:text-background"
          >
            <span aria-hidden="true">&#8594;</span>
          </button>
        </div>

        <div className="col-span-2 flex gap-1.5" aria-hidden="true">
          {slides.map((slide, i) => (
            <button
              key={slide.src}
              type="button"
              tabIndex={-1}
              onClick={() => embla?.scrollTo(i)}
              className="group relative h-6 flex-1"
            >
              <span className="absolute inset-x-0 top-1/2 block h-[2px] -translate-y-1/2 bg-border" />
              <span
                key={i === current ? `on-${current}-${auto}` : "off"}
                className={cn(
                  "absolute left-0 top-1/2 block h-[2px] -translate-y-1/2 bg-foreground",
                  i < current && "w-full",
                  i > current && "w-0",
                  i === current && !auto && "w-full"
                )}
                style={
                  i === current && auto
                    ? { animation: `carousel-fill ${INTERVAL}ms linear forwards` }
                    : undefined
                }
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  )
}
